import { PhoneNumberInput } from "@jasper/forms";
import { Button, Group, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { useState } from "react";
import { useUserProfile } from "@jasper/use-cases";

interface PhoneUpdateModalContentProps {
  onCancel: () => void;
  onSubmit: (phoneNumber: string) => Promise<boolean>;
}

const PhoneUpdateModalContent = ({
  onCancel,
  onSubmit,
}: PhoneUpdateModalContentProps) => {
  const [phoneUpdate, setPhoneUpdate] = useState<string>();
  const [isPhoneNumberInvalid, setIsPhoneNumberInvalid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    if (!phoneUpdate) {
      setIsPhoneNumberInvalid(true);
      return;
    }

    setIsSubmitting(true);
    const success = await onSubmit(phoneUpdate);
    setIsSubmitting(false);

    if (!success) {
      setIsPhoneNumberInvalid(true);
    }
  };

  return (
    <Stack gap="xl">
      <Text>
        {
          "Afin d'activer les notifications par SMS, veuillez mettre à jour votre numéro de téléphone."
        }
      </Text>
      <PhoneNumberInput
        label="Numéro de téléphone"
        value={phoneUpdate}
        onChange={phone => {
          setPhoneUpdate(phone);
          setIsPhoneNumberInvalid(false);
        }}
        error={
          isPhoneNumberInvalid ? "Numéro de téléphone invalide" : undefined
        }
      />
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>
          Annuler
        </Button>
        <Button loading={isSubmitting} onClick={() => void handleConfirm()}>
          Mettre à jour
        </Button>
      </Group>
    </Stack>
  );
};

export const usePhoneNumberUpdateModal = () => {
  const { updateProfile } = useUserProfile();

  const promptPhoneNumberUpdateModal = () => {
    return new Promise<boolean>(resolve => {
      let isResolved = false;

      const resolveOnce = (result: boolean) => {
        if (isResolved) {
          return;
        }

        isResolved = true;
        resolve(result);
      };

      const closeModal = () => {
        modals.closeAll();
      };

      modals.open({
        id: "phone-number-update-modal",
        title: <Text fw={700}>Numéro de téléphone requis</Text>,
        centered: true,
        onClose: () => resolveOnce(false),
        children: (
          <PhoneUpdateModalContent
            onCancel={() => {
              resolveOnce(false);
              closeModal();
            }}
            onSubmit={async phoneNumber => {
              try {
                const success = await updateProfile({
                  catchInputErrors: true,
                  data: {
                    phoneNumber,
                  },
                });

                if (success) {
                  resolveOnce(true);
                  closeModal();
                  return true;
                } else {
                  return false;
                }
              } catch {
                return false;
              }
            }}
          />
        ),
      });
    });
  };

  return { promptPhoneNumberUpdateModal };
};
